import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import RichTextEditor from "../../components/RichTextEditor";
import RichTextRenderer from "../../components/RichTextRenderer";
import ImageUpload from "../../components/ImageUpload";
import { Plus, Pencil, Trash2, ExternalLink, Copy, ChevronUp, ChevronDown, Save } from "lucide-react";

export default function AdminCoursesList() {
  const [courses, setCourses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", seo_subtitle: "", short_description: "", price_eur: 0, category: "", cover_image: "" });

  const load = () => api.get("/courses/admin/all").then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/courses", { ...form, seo_subtitle: form.seo_subtitle || "", price_eur: parseFloat(form.price_eur) || 0 });
    setCreating(false);
    setForm({ title: "", seo_subtitle: "", short_description: "", price_eur: 0, category: "", cover_image: "" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette formation ?")) return;
    await api.delete(`/courses/${id}`);
    await load();
    window.location.reload();
  };

  const duplicate = async (id) => {
    if (!window.confirm("Dupliquer cette formation complète ? La copie sera créée en brouillon.")) return;
    await api.post(`/courses/${id}/duplicate`);
    await load();
  };

  const moveCourse = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= courses.length) return;
    const next = [...courses];
    [next[index], next[target]] = [next[target], next[index]];
    setCourses(next);
  };

  const saveOrder = async () => {
    await api.patch("/courses/admin/reorder", courses.map((course, index) => ({
      course_id: course.course_id,
      display_order: (index + 1) * 10,
    })));
    await load();
    alert("Ordre des formations mis à jour.");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="admin-courses-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-3xl font-black">Formations</h1>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <button onClick={saveOrder} className="btn-ghost" data-testid="admin-save-course-order-btn">
            <Save className="w-4 h-4" /> Enregistrer l’ordre
          </button>
          <button onClick={() => setCreating(!creating)} className="btn-primary" data-testid="admin-create-course-btn">
            <Plus className="w-4 h-4" /> Nouvelle formation
          </button>
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="card p-6 mb-6 space-y-3" data-testid="admin-course-create-form">
          <h2 className="font-heading text-lg font-bold">Créer une formation</h2>
          <input className="input" required placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" />
          <input className="input" placeholder="Sous-titre SEO / angle problème (ex : Programme pour reprendre confiance et sortir des blocages émotionnels)" value={form.seo_subtitle} onChange={(e) => setForm({ ...form, seo_subtitle: e.target.value })} data-testid="course-seo-subtitle-input" />
          <RichTextEditor value={form.short_description} onChange={(value) => setForm({ ...form, short_description: value })} placeholder="Description courte" minHeight={120} />
          <div className="grid grid-cols-2 gap-3">
            <input className="input" type="number" step="0.01" min="0" placeholder="Prix EUR" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} data-testid="course-price-input" />
            <input className="input" placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="course-category-input" />
          </div>
          <ImageUpload
            label="Image de présentation"
            type="image"
            storage="upload"
            uploadContext="course"
            seoSlug=""
            seoTitle={form.title || ""}
            value={form.cover_image}
            onChange={(url) => setForm({ ...form, cover_image: url })}
          />
          <div className="flex gap-2">
            <button className="btn-primary" type="submit" data-testid="course-submit-create">Créer</button>
            <button className="btn-ghost" type="button" onClick={() => setCreating(false)}>Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {courses.map((c, index) => (
          // Mise en page mobile d'abord : le contenu occupe toute la largeur et
          // les actions passent sur leur propre ligne. Sur grand ecran, les
          // actions reviennent a droite. Sans ca, les boutons compriment le
          // titre jusqu'a un mot par ligne sur telephone.
          <div key={c.course_id} className="card p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4" data-testid={`admin-course-row-${c.course_id}`}>
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="flex shrink-0 flex-col gap-1">
                <button type="button" onClick={() => moveCourse(index, -1)} disabled={index === 0} className="rounded-lg border border-emerald-100 p-1 disabled:opacity-30" title="Monter"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => moveCourse(index, 1)} disabled={index === courses.length - 1} className="rounded-lg border border-emerald-100 p-1 disabled:opacity-30" title="Descendre"><ChevronDown className="w-4 h-4" /></button>
              </div>
              {c.cover_image && <img src={c.cover_image} alt="" className="w-20 h-14 shrink-0 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold leading-snug line-clamp-2 break-words">{c.title}</p>
                {c.seo_subtitle && <p className="mt-0.5 text-sm font-semibold text-brand-green line-clamp-1">{c.seo_subtitle}</p>}
                <RichTextRenderer html={c.short_description} className="text-sm text-gray-500 line-clamp-1" />
                <p className="mt-1 text-xs text-gray-400">{(c.modules || []).length} modules · {(c.modules || []).reduce((s, m) => s + (m.lessons || []).length, 0)} leçons · {c.price_eur}€</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-emerald-100 pt-3 lg:shrink-0 lg:border-t-0 lg:pt-0">
              <button onClick={() => duplicate(c.course_id)} className="btn-ghost" data-testid={`admin-duplicate-course-${c.course_id}`}><Copy className="w-4 h-4" /> Dupliquer</button>
              <Link to={`/admin/courses/${c.course_id}`} className="btn-ghost" data-testid={`admin-edit-course-${c.course_id}`}><Pencil className="w-4 h-4" /> Éditer</Link>
              <Link to={`/formations/${c.slug || c.course_id}`} className="btn-ghost" target="_blank" title="Voir la page publique"><ExternalLink className="w-4 h-4" /></Link>
              <button onClick={() => remove(c.course_id)} className="ml-auto p-2 text-red-500 hover:text-red-700 lg:ml-0" title="Supprimer" data-testid={`admin-delete-course-${c.course_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
